import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { AiDtosKeysEnum } from '@zaoblako/shared/modules/ai/enums/ai-dtos-keys.enum';
import { SecurityDtoKeysEnum } from '@zaoblako/shared/modules/security/enums/security-dto-keys.enum';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import type { AgentFindInterface } from '@zaoblako/shared/modules/ai/dtos/agent/agent-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { ChatGroupFindInterface } from '@zaoblako/shared/modules/ai/dtos/chat-group/chat-group-find.dto';

import * as yup from 'yup';

/** FRM AI chat response body - DTO signature */
export interface ChatFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Creation date and time */
  created: Date;
  /** Last update date and time */
  updated: Date;
  /** Soft deletion update date and time. Chat will be permanently deleted six months after this date. */
  deleted?: Date | null;
  /** Chat display name */
  name: string;
  /** Order index for list (lower = first) */
  order: number;
  /** Optional group (folder) */
  group?: ChatGroupFindInterface;
  /** User associated with this chat */
  user: UserFindInterface;
  /** Additional chat participants */
  userAuditors?: UserFindInterface[];
  /** Additional chat participant groups */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Agent associated with this chat */
  agent: AgentFindInterface;
  /** Commentary thread associated with this chat */
  thread?: ThreadFindInterface;
}

/** FRM AI chat response body - DTO schema */
export const ChatFindDto = SerdeDtoClass.create<ChatFindInterface>(AiDtosKeysEnum.AI_CHAT_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    sortable: true,
    label: 'Изменён',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удалён',
    serializer: yup.date(),
  },
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  order: {
    optional: true,
    sortable: true,
    label: 'Порядок',
    serializer: yup.number(),
  },
  group: {
    optional: true,
    label: 'Группа',
    nested: AiDtosKeysEnum.AI_CHAT_GROUP_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  user: {
    sortable: true,
    label: 'Пользователь',
    nested: SecurityDtoKeysEnum.USER_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  userAuditors: {
    optional: true,
    sortable: true,
    label: 'Аудиторы',
    nested: SecurityDtoKeysEnum.USER_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  userGroupAuditors: {
    optional: true,
    sortable: true,
    label: 'Группы аудиторов',
    nested: SecurityDtoKeysEnum.USER_GROUP_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  agent: {
    sortable: true,
    label: 'Агент',
    nested: CatalogDtosKeysEnum.AGENT_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  thread: {
    optional: true,
    label: 'Тема комментариев',
    nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
    serializer: (_, nested) => nested,
  },
});

